/** Operations → Safety meeting: who confirmed today's video, when, from where. Owners/admins/superadmins. */
import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, ShieldX, MapPin, RefreshCw, Loader2 } from "lucide-react";
import SafetyExport from "./SafetyExport";
import { CertReminders, QuizStats } from "./SafetyInsights";
import api from "@/lib/api";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

export default function SafetyLogTab() {
  const [data, setData] = useState(null);
  const [day, setDay] = useState("");
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/safety/log", { params: day ? { day } : { days: 30 } }); setData(data); }
    catch { setData({ rows: [], missing_today: [] }); }
    finally { setLoading(false); }
  }, [day]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="safety-log-tab">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="mr-auto text-sm font-bold text-white">Safety meeting log</h3>
        <input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="rounded-md border border-white/15 bg-white/5 px-2 py-1 text-xs text-white" data-testid="safety-log-day" />
        {day && <button onClick={() => setDay("")} className="text-xs text-white/60 underline" data-testid="safety-log-clear">Last 30 days</button>}
        <button onClick={load} className="rounded-md border border-white/15 p-1.5 text-white/70" aria-label="Refresh" data-testid="safety-log-refresh">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}</button>
      </div>
      <SafetyExport day={day} />
      <CertReminders />
      <QuizStats />

      {data && !day && (
        <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-3 text-xs" data-testid="safety-missing-today">
          <div className="font-semibold text-amber-200">Not yet confirmed today ({data.missing_today.length})</div>
          <div className="mt-1 text-amber-100/80">{data.missing_today.length ? data.missing_today.map((m) => m.name).join(", ") : "Everyone has confirmed."}</div>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-left text-xs text-white/80">
          <thead className="bg-white/5 font-mono text-[10px] uppercase tracking-widest text-white/50">
            <tr><th className="px-3 py-2">Employee</th><th className="px-3 py-2">When</th><th className="px-3 py-2">Result</th><th className="px-3 py-2">IP</th><th className="px-3 py-2">Location</th><th className="px-3 py-2">Watched</th></tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.log_id} className="border-t border-white/5" data-testid={`safety-log-row-${r.log_id}`}>
                <td className="px-3 py-2 font-semibold text-white">{r.user_name}</td>
                <td className="px-3 py-2 whitespace-nowrap">{fmt(r.at)}</td>
                <td className="px-3 py-2">{r.action === "confirm"
                  ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 font-semibold text-emerald-300"><ShieldCheck className="h-3 w-3" /> Confirmed{r.quiz ? ` · quiz ${r.quiz.correct}/${r.quiz.total}` : ""}</span>
                  : r.action === "quiz_fail"
                    ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 font-semibold text-amber-300"><ShieldX className="h-3 w-3" /> Quiz failed {r.quiz?.correct}/{r.quiz?.total}</span>
                    : <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 font-semibold text-red-300"><ShieldX className="h-3 w-3" /> Denied</span>}</td>
                <td className="px-3 py-2 font-mono">{r.ip || "—"}</td>
                <td className="px-3 py-2">{r.location
                  ? <a href={`https://maps.google.com/?q=${r.location.lat},${r.location.lng}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sky-300 underline"><MapPin className="h-3 w-3" />{r.location.lat.toFixed(4)}, {r.location.lng.toFixed(4)}{r.location.accuracy_m ? ` (±${Math.round(r.location.accuracy_m)} m)` : ""}</a>
                  : <span className="text-white/40">{r.location_error ? `unavailable (${r.location_error})` : "—"}</span>}</td>
                <td className="px-3 py-2 font-mono">{r.watched_seconds != null ? `${Math.round(r.watched_seconds)}s` : "—"}</td>
              </tr>
            ))}
            {data && !data.rows.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-white/40">No safety meeting records{day ? " for that day" : " yet"}.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
